import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  findCountry,
  popularCountries,
  otherCountries,
  filterCountries,
} from "../../Utils/countries";

/**
 * Full-width country picker for the WhatsApp-OTP login (design frames 8 / 8b in
 * storefront-redesign-login-otp-v1.html).
 *
 * Rendered via a portal to <body> at a high z-index so it "escapes whatever
 * launched it" — the checkout LoginSheet (fixed sheet) OR the narrow nav drawer
 * (an antd Drawer at z-index 1000). Presentational: the caller owns the selected
 * country, this just lists / filters / reports selection.
 *
 * India default pinned under "Selected"; "Popular" = diaspora markets; then the
 * alphabetical "All countries". Type-to-filter matches name or dial code, with
 * the matched name text highlighted.
 */

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="w-[15px] h-[15px] text-ink-400 flex-shrink-0"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-[17px] h-[17px]"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Wrap the matched substring of `name` in a highlight span (design 8b).
const highlightName = (name, query) => {
  const q = (query || "").trim();
  if (!q) return name;
  const i = name.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return name;
  return (
    <>
      {name.slice(0, i)}
      <span className="bg-[#FFF1C2] rounded-[2px]">
        {name.slice(i, i + q.length)}
      </span>
      {name.slice(i + q.length)}
    </>
  );
};

const GroupLabel = ({ children }) => (
  <div className="text-[10px] font-[500] tracking-[0.07em] uppercase text-ink-400 px-1.5 pt-3 pb-1">
    {children}
  </div>
);

const CountryRow = ({ country, selected, query, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(country.code)}
    className={`flex items-center gap-3 w-full px-2 py-[11px] rounded-[10px] text-left ${
      selected ? "bg-s-tint" : ""
    }`}
  >
    <span className="text-[21px] leading-none w-[26px] text-center flex-shrink-0">
      {country.flag}
    </span>
    <span className="flex-1 text-[13.5px] font-[500] text-ink-900">
      {highlightName(country.name, query)}
    </span>
    <span
      className={`text-[12.5px] font-[500] rounded-md px-2 py-[3px] ${
        selected ? "bg-[#e6eaff] text-s-primary-d" : "bg-[#F5F5F5] text-ink-500"
      }`}
    >
      +{country.dial}
    </span>
    <span
      className={`w-[18px] grid place-items-center flex-shrink-0 ${selected ? "text-s-primary" : "text-transparent"}`}
    >
      <CheckIcon />
    </span>
  </button>
);

const CountryPicker = ({ open, onClose, selectedCode, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  // Fresh search each open. Deliberately NOT auto-focusing the field: focusing it
  // pops the mobile keyboard the instant the sheet opens, covering the country
  // list. The user taps the search box when they actually want to type.
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  if (!open) return null;

  const handleSelect = (code) => {
    onSelect(code);
    onClose();
  };

  const trimmed = query.trim();
  const results = filterCountries(query);
  const selected = findCountry(selectedCode);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[1200] bg-[rgba(18,20,32,.46)]"
        onClick={onClose}
      />
      <div className="fixed left-0 right-0 bottom-0 z-[1201] max-w-[480px] mx-auto bg-white rounded-t-[22px] shadow-[0_-12px_40px_rgba(0,0,0,.24)] flex flex-col max-h-[85dvh]">
        <div className="w-[38px] h-1 rounded bg-[#D5D8E0] mx-auto mt-2 mb-1 flex-shrink-0" />

        <div className="px-4 pb-2 flex-shrink-0">
          <div className="flex items-center justify-between my-0.5 mb-3">
            <span className="text-[17px] font-[500] text-ink-900">
              {t("countryPicker.title")}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("countryPicker.close")}
              className="w-[26px] h-[26px] rounded-full bg-[#F5F5F5] grid place-items-center text-ink-500"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                className="w-[13px] h-[13px]"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div
            className={`flex items-center gap-2.5 border-[1.5px] rounded-md px-3 py-[11px] ${
              trimmed
                ? "border-s-primary bg-white shadow-[0_0_0_3px_rgba(72,98,238,.12)]"
                : "border-wc-border bg-[#F5F5F5]"
            }`}
          >
            <SearchIcon />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("countryPicker.searchPlaceholder")}
              className="flex-1 min-w-0 text-[13.5px] font-[400] text-ink-900 outline-none bg-transparent placeholder:text-ink-400"
            />
            {trimmed && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label={t("countryPicker.clear")}
                className="w-[17px] h-[17px] rounded-full bg-[#c2c7d6] grid place-items-center flex-shrink-0"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  className="w-[9px] h-[9px] text-white"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-3 pb-2">
          {trimmed ? (
            <>
              <GroupLabel>
                {t("countryPicker.results", { count: results.length })}
              </GroupLabel>
              {results.length === 0 ? (
                <p className="text-[12.5px] text-ink-400 px-2 py-3">
                  {t("countryPicker.noMatch", { query: trimmed })}
                </p>
              ) : (
                results.map((c) => (
                  <CountryRow
                    key={c.code}
                    country={c}
                    selected={c.code === selectedCode}
                    query={query}
                    onSelect={handleSelect}
                  />
                ))
              )}
            </>
          ) : (
            <>
              <GroupLabel>{t("countryPicker.selected")}</GroupLabel>
              <CountryRow country={selected} selected onSelect={handleSelect} />

              <GroupLabel>{t("countryPicker.popular")}</GroupLabel>
              {popularCountries(selectedCode).map((c) => (
                <CountryRow key={c.code} country={c} onSelect={handleSelect} />
              ))}

              <GroupLabel>{t("countryPicker.allCountries")}</GroupLabel>
              {otherCountries(selectedCode).map((c) => (
                <CountryRow key={c.code} country={c} onSelect={handleSelect} />
              ))}
            </>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default CountryPicker;
